/**
 * Phase 7.1 (partial start) — error boundary around the lazy-route
 * `<Suspense>` subtree.
 *
 * Why this exists: Phase 7.3 made every off-warmup-path route lazy.
 * After a deploy, an old browser tab navigating to a lazy route
 * fetches a chunk hash that no longer exists on the CDN — the
 * `import()` rejects, React surfaces the rejection as a thrown
 * error inside Suspense, and without a boundary the user gets
 * stuck on an infinite spinner. This boundary catches the failure,
 * logs to Sentry, and renders a "Recarregar página" CTA.
 *
 * Catches any other render error inside `<main>` too, with a
 * generic fallback. Sits inside the root `Sentry.ErrorBoundary`
 * (App.tsx) which still catches errors outside the route subtree
 * (chrome, providers, etc.).
 *
 * The exported `RouteErrorBoundary` is a functional wrapper that
 * resets `state.error` on rail navigation by forwarding
 * `useLocation().pathname` as the key to the underlying class —
 * see the wrapper's docstring at the bottom of this file.
 */

import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * True when `err` looks like a dynamic-import failure.
 *
 * Heuristic — both Webpack and Vite throw recognizable shapes:
 *  - Webpack: `error.name === "ChunkLoadError"`.
 *  - Vite/Rollup: `error.message` contains "Failed to fetch
 *    dynamically imported module" or the alternate browser-engine
 *    phrasing "Importing a module script failed".
 *
 * Pure so the rule is unit-testable without mounting React.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === "ChunkLoadError") return true;
  const msg = err.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

/** Subset of i18next's TFunction signature we actually use — keeps
 *  the class boundary free of any specific i18next-version typing
 *  while still type-safe at call sites. */
type TranslateFn = (key: string) => string;

interface ClassProps extends Props {
  t: TranslateFn;
}

/**
 * Functional wrapper that remounts the underlying class boundary on
 * route change — `state.error` resets so a chunk-failure on
 * `/research` doesn't leave the fallback on screen after the user
 * clicks `/home` in the rail.
 *
 * Class boundaries can't read `useLocation()` directly. Forwarding
 * `key={location.pathname}` is the standard pattern: React tears
 * down + recreates the inner component when the key flips, which
 * also clears `state`.
 */
export function RouteErrorBoundary(props: Props) {
  const location = useLocation();
  const { t } = useTranslation();
  // Pass `t` as a prop so the class boundary stays render-tree-stable
  // (re-rendering on every locale flip is fine — class boundaries
  // don't need to re-mount). The key={location.pathname} keeps the
  // rail-navigation reset behavior described in the file header.
  // Narrow i18next's overload-rich TFunction to the (key) => string
  // shape we actually use — keeps the boundary free of i18next
  // version-specific typing without a double-cast.
  const translate: TranslateFn = (key) => t(key);
  return (
    <RouteErrorBoundaryClass key={location.pathname} t={translate}>
      {props.children}
    </RouteErrorBoundaryClass>
  );
}

class RouteErrorBoundaryClass extends Component<ClassProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        source: "route-error-boundary",
        chunk_load_error: String(isChunkLoadError(error)),
      },
      extra: { componentStack: info.componentStack },
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { t } = this.props;
    const isChunkError = isChunkLoadError(error);
    return (
      <div
        data-testid={isChunkError ? "route-error-chunk" : "route-error-generic"}
        className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <AlertTriangle size={24} className="text-amber-500" />
        <p className="text-sm text-slate-700 dark:text-slate-300">
          {isChunkError ? t("errors.chunkOutdated") : t("errors.routeGeneric")}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <RefreshCw size={14} />
          {t("errors.reloadPage")}
        </button>
      </div>
    );
  }
}
